import type {
  ClosedReason,
  Locale,
  Member,
  MeResponse,
  PlayerNode,
  PublicConfig,
  QueueItem,
  QueueNode,
  SessionMeta,
  SongDTO,
  ToneName,
  TvState,
} from '@akpl/shared';
import { isAkpError, personName } from '@akpl/shared';
import { create, type StoreApi, type UseBoundStore } from 'zustand';

import { api } from './api';
import { greetingName } from './lib/greeting';

export type BootStatus = 'idle' | 'booting' | 'ready' | 'error';
export type AuthStatus = 'unknown' | 'signed_out' | 'signed_in';
export type ToastTone = 'info' | 'success' | 'error';

export interface ToastItem {
  id: number;
  message: string;
  tone: ToastTone;
  ttlMs: number;
}

/** A queue item plus its RTDB key. */
export interface QueueEntry extends QueueItem {
  id: string;
}

/**
 * The queue item the sheet is editing, when it was opened from a queue row (QA iteration 3,
 * M6): the sheet then starts on the tone that was chosen when the song was added and saves,
 * plays or removes **that** item instead of enqueueing a new one.
 */
export interface SheetItem {
  id: string;
  tone: ToneName;
  /** Whether this viewer may delete it (the host, or the item's owner — docs/03 §2). */
  canRemove: boolean;
}

/** What the song bottom sheet (Figma M-C2 50:759) is showing. */
export interface SheetState {
  song: SongDTO;
  /** Editorial context line under the artists ("· Clásicos del rock"), optional. */
  context?: string;
  item?: SheetItem;
}

export interface MobileState {
  boot: { status: BootStatus; error: string | null };
  config: PublicConfig | null;
  locale: Locale;
  auth: { status: AuthStatus; uid: string | null; isAnonymous: boolean };
  /** `GET /me`; `activeSession` decides whether `/app` is reachable at all. */
  me: MeResponse | null;
  sid: string | null;
  meta: SessionMeta | null;
  members: Record<string, Member>;
  queue: { items: Record<string, QueueItem>; order: string[] };
  player: PlayerNode | null;
  tvState: TvState | null;
  connected: boolean;
  toasts: ToastItem[];
  sheet: SheetState | null;
  /**
   * "Your session ended, and this is why" (QA 2026-09-15 UX-566). Set by `endSession` **after**
   * `clearSession`, so it survives the `/app` → `/guest` navigation the phone is about to make
   * and `/guest` can explain the bounce; `reason` is `null` when the API said nothing.
   */
  endedNotice: { reason: ClosedReason | null } | null;
}

export interface MobileActions {
  setBoot(status: BootStatus, error?: string | null): void;
  setConfig(config: PublicConfig | null): void;
  setLocale(locale: Locale): void;
  setAuth(auth: MobileState['auth']): void;
  setMe(me: MeResponse | null): void;
  setSid(sid: string | null): void;
  setMeta(meta: SessionMeta | null): void;
  setMembers(members: Record<string, Member> | null): void;
  setQueue(node: QueueNode | null | undefined): void;
  setPlayer(player: PlayerNode | null): void;
  setTvState(tvState: TvState | null): void;
  setConnected(connected: boolean): void;
  /** One snapshot of every live node, written by the `/app` shell (docs/05 §4). */
  setLive(live: {
    meta: SessionMeta | null;
    queue: QueueNode | null;
    player: PlayerNode | null;
    members: Record<string, Member> | null;
    tvState: TvState | null;
    connected: boolean;
  }): void;
  /** Remembers why the phone was returned to `/guest` (UX-566). */
  noteSessionEnded(reason: ClosedReason | null): void;
  dismissSessionEnded(): void;
  showToast(message: string, tone?: ToastTone, ttlMs?: number): number;
  dismissToast(id: number): void;
  openSheet(sheet: SheetState): void;
  closeSheet(): void;
  /** Drops everything session-shaped (leave, logout, expiry). */
  clearSession(): void;
}

export type MobileStore = MobileState & MobileActions;
export type MobileStoreApi = UseBoundStore<StoreApi<MobileStore>>;

export const TOAST_TTL_MS = 3000;
let toastSeq = 0;

/**
 * UX-400: a session opened before the name fix carries the host's e-mail local part in every
 * RTDB `addedBy.name` and in `meta.hostName`, so the phone printed "Pidió carlos+dev" on every
 * queue row and "Anfitrión carlos+dev" on the Profile card. Migration `0010` cleans the stored
 * rows and the API no longer writes them, but a live session's node is a snapshot taken when it
 * started — so the same rule is applied on the way into the store, once, for every screen.
 */
function withPersonNames(node: Record<string, QueueItem> | null | undefined) {
  const out: Record<string, QueueItem> = {};
  for (const [id, item] of Object.entries(node ?? {})) {
    const name = personName(item.addedBy.name);
    out[id] = name === item.addedBy.name ? item : { ...item, addedBy: { ...item.addedBy, name } };
  }
  return out;
}

export function sortQueueIds(items: Record<string, QueueItem>): string[] {
  return Object.keys(items).sort((a, b) => {
    const d = items[a]!.order - items[b]!.order;
    return d !== 0 ? d : a < b ? -1 : a > b ? 1 : 0;
  });
}

export const initialState: MobileState = {
  boot: { status: 'idle', error: null },
  config: null,
  locale: 'es',
  auth: { status: 'unknown', uid: null, isAnonymous: false },
  me: null,
  sid: null,
  meta: null,
  members: {},
  queue: { items: {}, order: [] },
  player: null,
  tvState: null,
  connected: true,
  toasts: [],
  sheet: null,
  endedNotice: null,
};

/**
 * One zustand store for the whole companion (docs/05 §1). `routes/AppShell.tsx` mirrors
 * `useMe()` and the RTDB listeners of `hooks/useSession.ts` into it with `setLive`; screens
 * read with selectors and never subscribe themselves. `createMobileStore()` gives tests an isolated instance.
 */
export function createMobileStore(): MobileStoreApi {
  return create<MobileStore>()((set) => ({
    ...initialState,
    setBoot: (status, error = null) => set({ boot: { status, error } }),
    setConfig: (config) => set({ config }),
    setLocale: (locale) => set({ locale }),
    setAuth: (auth) => set({ auth }),
    setMe: (me) => set({ me }),
    setSid: (sid) => set({ sid }),
    setMeta: (meta) => set({ meta }),
    setMembers: (members) => set({ members: members ?? {} }),
    setQueue: (node) => {
      const items = withPersonNames(node);
      set({ queue: { items, order: sortQueueIds(items) } });
    },
    setPlayer: (player) => set({ player }),
    setTvState: (tvState) => set({ tvState }),
    setConnected: (connected) => set({ connected }),
    setLive: ({ meta, queue, player, members, tvState, connected }) => {
      const items = withPersonNames(queue);
      set({
        meta: meta ? { ...meta, hostName: personName(meta.hostName) } : meta,
        queue: { items, order: sortQueueIds(items) },
        player,
        members: members ?? {},
        tvState,
        connected,
      });
    },
    noteSessionEnded: (reason) => set({ endedNotice: { reason } }),
    dismissSessionEnded: () => set({ endedNotice: null }),
    showToast: (message, tone = 'info', ttlMs = TOAST_TTL_MS) => {
      toastSeq += 1;
      const id = toastSeq;
      set((s) => ({ toasts: [...s.toasts.slice(-2), { id, message, tone, ttlMs }] }));
      return id;
    },
    dismissToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
    openSheet: (sheet) => set({ sheet }),
    closeSheet: () => set({ sheet: null }),
    clearSession: () =>
      set({
        me: null,
        sid: null,
        meta: null,
        members: {},
        queue: { items: {}, order: [] },
        player: null,
        tvState: null,
        sheet: null,
        // A leave or a logout is not something to explain on `/guest`; `endSession` writes the
        // notice back right after this when there is something to say (UX-566).
        endedNotice: null,
      }),
  }));
}

export const useStore: MobileStoreApi = createMobileStore();

/* ------------------------------------------------------------------ selectors + helpers */

export function queueEntries(state: MobileState['queue']): QueueEntry[] {
  return state.order.map((id) => ({ id, ...state.items[id]! }));
}

/** Tab badge = `queued` items only (docs/05 §4). */
export const selectQueuedCount = (s: MobileStore): number => {
  let n = 0;
  for (const id of s.queue.order) if (s.queue.items[id]?.status === 'queued') n += 1;
  return n;
};

/**
 * The caller's **own** queued or playing copy of a song, or `null` (QA 2026-09-15 UX-582).
 *
 * The API refuses a second copy of the same song by the same uid while the first is `queued` or
 * `playing` (`409 duplicate_in_queue`, `services/queue/index.ts`), and the sheet opened from
 * Buscar knew nothing about it: it offered «Agregar a la fila», spent 1.4 s behind the blocking
 * loader and came back refused, every single time. The queue the phone already has in the store
 * answers the question before the button is drawn — it is the same lookup the Fila screen does.
 */
export const selectOwnQueuedId =
  (songId: string) =>
  (s: MobileStore): string | null => {
    const uid = s.auth.uid;
    if (!uid) return null;
    for (const id of s.queue.order) {
      const item = s.queue.items[id];
      if (
        item &&
        item.songId === songId &&
        item.addedBy.uid === uid &&
        (item.status === 'queued' || item.status === 'playing')
      ) {
        return id;
      }
    }
    return null;
  };

export const selectPlayingId = (s: MobileStore): string | null => {
  for (const id of s.queue.order) if (s.queue.items[id]?.status === 'playing') return id;
  return null;
};

/**
 * The features that gate this session. The RTDB `meta.features` snapshot is authoritative for
 * everyone in the room (guests inherit the host, docs/07 §1); `/me` is the fallback while the
 * listener has not delivered `meta` yet.
 */
export const selectFeatures = (s: MobileStore): readonly string[] =>
  s.meta?.features ?? s.me?.features ?? [];

export const selectIsHost = (s: MobileStore): boolean => s.me?.activeSession?.role === 'host';

export const selectUid = (s: MobileStore): string | null => s.auth.uid;

/**
 * How this phone's own user is called, or `null` when nothing is known yet (the caller adds
 * the "Invitado" placeholder).
 *
 * A guest types a name on the join form, so "Invitado" is never the right answer for someone
 * sitting in a session (QA iteration 3, M4). The account's `display_name` comes first, and the
 * live RTDB `members/{uid}` node — written by `POST /sessions/join` with exactly that name —
 * is the fallback while `/me` has not caught up.
 */
export const selectDisplayName = (s: MobileStore): string | null => {
  // UX-400: the same guard as the greeting — a stored `display_name` that is the address local
  // part is not a name, and it is this selector the Profile header and its editor pre-fill from.
  return greetingName({
    displayName: s.me?.user.displayName,
    memberName: s.auth.uid ? s.members[s.auth.uid]?.name : null,
    email: s.me?.user.email,
  });
};

/**
 * The name the Home greeting says, or `null` for "greet without a name" (QA 2026-09-15 UX-125,
 * 2026-09-16 UX-400). Since UX-400 this is the same question as {@link selectDisplayName} —
 * "what do we call this phone's user" and "is there a name worth greeting" had different
 * answers only while the rest of the phone was willing to print an e-mail local part, and it no
 * longer is. Both go through `lib/greeting.ts`, which goes through `@akpl/shared`'s
 * `personName` — the same helper the API resolves every name with.
 */
export const selectGreetingName = (s: MobileStore): string | null => {
  const uid = s.auth.uid;
  return greetingName({
    displayName: s.me?.user.displayName,
    memberName: uid ? s.members[uid]?.name : null,
    email: s.me?.user.email,
  });
};

/**
 * `GET /me` straight into the store, for the screens that change it (Profile's `PATCH /me`,
 * the photo upload, leaving a session). The `/app` shell also mirrors TanStack Query's `/me`
 * into the store, so both paths converge on the same value.
 *
 * A 401/404 is "no session" (an anonymous user who never joined has no `users` row yet on some
 * paths), not a failure.
 */
export async function refreshMe(store: MobileStoreApi): Promise<string | null> {
  const s = () => store.getState();
  try {
    const me = await api.me();
    s().setMe(me);
    const sid = me.activeSession?.id ?? null;
    s().setSid(sid);
    return sid;
  } catch (err) {
    if (isAkpError(err) && (err.status === 404 || err.status === 401)) {
      s().setMe(null);
      s().setSid(null);
      return null;
    }
    throw err;
  }
}
